/**
 * What a cell *is* — `KLQ-20260828-001` §A.1.
 *
 * One module owns both directions between a `{ rowId, colKey }` pair and the string a `Set`
 * can hold, and **no consumer anywhere writes a regex over it**. That rule exists because the
 * reference implementation does the opposite and is one column key away from a silent bug:
 * ayon builds `cell-${rowId}-${colId}` and parses it back with `/^cell-(.+)-(.+)$/`
 * (`utils/cellUtils.ts:32,38`). The first group is greedy, so with UUID row ids — which are
 * nothing but dashes — the split lands on the *last* dash in the whole string. It works today
 * only because every one of their column ids happens to contain no dash. The day one does
 * (`due-at`, `link-out`), `rowId` quietly swallows half the column name and the cell resolves
 * to a neighbour.
 *
 * So the pair is the truth and the key is a derived detail: joined with `U+001F`, the ASCII
 * unit separator, which cannot occur in a UUID and cannot occur in a column key (the type
 * below refuses one, and `assertColKey` fails loudly in development if a caller invents one
 * at runtime).
 */

/** Where a cell is. The pair is the value that travels; the string is only a `Set` key. */
export interface CellRef {
  readonly rowId: string;
  readonly colKey: string;
}

/** A `CellRef` flattened for set membership. Opaque — build it here, read it back here. */
export type CellKey = string;

/**
 * The select column (`KLQ-20260825-006` §B.8). It is a column like any other so that "three
 * rows" and "nine cells" stay one mechanism, and it is named here rather than in the table
 * because all three mechanisms need to recognise it: selection treats it as a row proxy,
 * the clipboard refuses to copy it, and editing refuses to write it.
 */
export const SELECT_COLUMN_KEY = "__select__";

/** The one character that may not appear in either half. See the module note. */
const SEPARATOR = "\u001f";

export function cellKey(ref: CellRef): CellKey {
  return `${ref.rowId}${SEPARATOR}${ref.colKey}`;
}

/**
 * The key, back to the pair. Total and exact — a split on a character neither half can
 * contain has no ambiguity to resolve, which is the whole argument for the separator.
 */
export function cellRef(key: CellKey): CellRef {
  const at = key.indexOf(SEPARATOR);
  // Not reachable through `cellKey`. A caller that hand-built a key gets an obviously empty
  // column rather than a plausible-looking wrong one.
  if (at === -1) return { rowId: key, colKey: "" };
  return { rowId: key.slice(0, at), colKey: key.slice(at + 1) };
}

export function sameCell(a: CellRef | null, b: CellRef | null): boolean {
  if (a === null || b === null) return a === b;
  return a.rowId === b.rowId && a.colKey === b.colKey;
}

/**
 * A development-only guard on the one assumption the encoding rests on. It is cheap, it runs
 * where a bad key would be introduced (grid registration), and it turns "a cell resolves to
 * its neighbour three screens later" into a message at the call site.
 */
export function assertColKey(colKey: string): void {
  if (import.meta.env.DEV && colKey.includes(SEPARATOR)) {
    throw new Error(
      `A column key may not contain U+001F — it is the cell-key separator (${colKey}).`,
    );
  }
}
